import React, {
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { ArrowRight, CircleCheck, CircleX, Info, TriangleAlert, X } from "lucide-react";
import { MGSubtitle } from "../MGSubtitle";
import { MGText } from "../MGText";
import styles from "./MGToast.module.css";

export const mgToastTypes = ["info", "error", "success", "warn"] as const;
export type TMGToastType = (typeof mgToastTypes)[number];

export const mgToastPositions = [
  "top-left",
  "top-center",
  "top-right",
  "bottom-left",
  "bottom-center",
  "bottom-right",
] as const;
export type TMGToastPosition = (typeof mgToastPositions)[number];

export type TMGToastOptions = {
  dataTestId?: string;
  /** Keeps the toast open so interactive content can be used. */
  hasInlineLink?: boolean;
  /** Adds a link after the message and keeps the toast open. */
  link?: { text: string; url: string };
  message: ReactNode;
  position?: TMGToastPosition;
  /** Allows the toast to be dismissed with a horizontal swipe. */
  swipeToDismiss?: boolean;
  title?: string;
};

type TToastRecord = {
  autoClose: boolean;
  id: number;
  options: TMGToastOptions;
  position: TMGToastPosition;
  type: TMGToastType;
};

type TToastListener = (toasts: TToastRecord[]) => void;

const toastIcons = {
  info: Info,
  error: CircleX,
  success: CircleCheck,
  warn: TriangleAlert,
};

let nextToastId = 0;
let activeToasts: TToastRecord[] = [];
const listeners = new Set<TToastListener>();

const emitToasts = () => {
  const snapshot = [...activeToasts];
  listeners.forEach((listener) => listener(snapshot));
};

const dismissToast = (toastId?: number) => {
  activeToasts = toastId === undefined ? [] : activeToasts.filter(({ id }) => id !== toastId);
  emitToasts();
};

const showToast = (type: TMGToastType, options: TMGToastOptions) => {
  const id = ++nextToastId;
  const autoClose = !options.hasInlineLink && !options.link;

  activeToasts = [
    ...activeToasts,
    {
      autoClose,
      id,
      options,
      position: options.position ?? "top-right",
      type,
    },
  ];
  emitToasts();

  return id;
};

export const MGToast = {
  info: (options: TMGToastOptions) => showToast("info", options),
  error: (options: TMGToastOptions) => showToast("error", options),
  success: (options: TMGToastOptions) => showToast("success", options),
  warn: (options: TMGToastOptions) => showToast("warn", options),
  dismiss: dismissToast,
};

const ToastContent = ({ toast }: { toast: TToastRecord }) => {
  const { autoClose, id, options, type } = toast;
  const Icon = toastIcons[type];
  const surfaceRef = useRef<HTMLDivElement>(null);
  const dismissTimeoutRef = useRef<number>();
  const gestureRef = useRef<{
    axis?: "horizontal" | "vertical";
    pointerId: number;
    startX: number;
    startY: number;
  }>();
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const swipeToDismiss = options.swipeToDismiss !== false;

  useEffect(() => {
    if (!autoClose) return;

    const timeoutId = window.setTimeout(() => dismissToast(id), 3000);
    return () => window.clearTimeout(timeoutId);
  }, [autoClose, id]);

  useEffect(
    () => () => {
      if (dismissTimeoutRef.current !== undefined) {
        window.clearTimeout(dismissTimeoutRef.current);
      }
    },
    [],
  );

  const resetGesture = () => {
    gestureRef.current = undefined;
    setIsDragging(false);
    setDragOffset(0);
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;

    if (!swipeToDismiss || !event.isPrimary || event.button !== 0 || target.closest("a, button")) {
      return;
    }

    gestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;

    if (!gesture.axis) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 8) return;
      gesture.axis = Math.abs(deltaX) > Math.abs(deltaY) ? "horizontal" : "vertical";
    }

    if (gesture.axis === "vertical") return;

    event.preventDefault();
    setIsDragging(true);
    setDragOffset(deltaX);
  };

  const handlePointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - gesture.startX;
    const surfaceWidth = surfaceRef.current?.getBoundingClientRect().width ?? 0;
    const dismissThreshold = Math.min(120, Math.max(64, surfaceWidth * 0.25));

    gestureRef.current = undefined;
    setIsDragging(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);

    if (gesture.axis === "horizontal" && Math.abs(deltaX) >= dismissThreshold) {
      const direction = deltaX < 0 ? -1 : 1;
      setIsExiting(true);
      setDragOffset(direction * (surfaceWidth + 32));
      dismissTimeoutRef.current = window.setTimeout(() => dismissToast(id), 180);
      return;
    }

    setDragOffset(0);
  };

  const handlePointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (gestureRef.current?.pointerId !== event.pointerId) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    resetGesture();
  };

  const surfaceWidth = surfaceRef.current?.getBoundingClientRect().width ?? 1;
  const dragOpacity = 1 - Math.min(0.6, (Math.abs(dragOffset) / surfaceWidth) * 0.6);

  return (
    <div
      ref={surfaceRef}
      className={styles.gestureSurface}
      data-dragging={isDragging ? "true" : undefined}
      data-exiting={isExiting ? "true" : undefined}
      data-toast-type={type}
      data-testid={options.dataTestId}
      role={type === "error" || type === "warn" ? "alert" : "status"}
      aria-live={type === "error" || type === "warn" ? "assertive" : "polite"}
      aria-atomic="true"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerCancel}
      style={{
        opacity: dragOpacity,
        transform: `translate3d(${dragOffset}px, 0, 0)`,
      }}>
      <div className={styles.toast} data-toast-type={type}>
        <Icon
          className={styles.icon}
          size={48}
          aria-hidden="true"
          focusable="false"
          data-toast-icon={type}
        />

        <div className={styles.middle}>
          <div className={styles.texts}>
            {options.title && (
              <MGSubtitle subtitleStyleDefinition="100-07" className={styles.title}>
                {options.title}
              </MGSubtitle>
            )}
            <MGText textStyleDefinition="body-100-03" className={styles.message}>
              {options.message}
            </MGText>
          </div>

          {options.link && (
            <a
              className={styles.link}
              href={options.link.url}
              target="_blank"
              rel="noopener noreferrer">
              {options.link.text}
              <ArrowRight size={20} aria-hidden="true" />
            </a>
          )}
        </div>

        {!autoClose && (
          <button
            type="button"
            className={styles.close}
            aria-label="Fechar notificação"
            title="Fechar notificação"
            onClick={() => dismissToast(id)}>
            <X size={24} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

const subscribeToToasts = (listener: TToastListener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const MGToastContainer = () => {
  const [toasts, setToasts] = useState<TToastRecord[]>(() => [...activeToasts]);

  useEffect(() => subscribeToToasts(setToasts), []);

  if (typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <>
      {mgToastPositions.map((position) => {
        const positionedToasts = toasts.filter((toast) => toast.position === position);
        if (positionedToasts.length === 0) return null;

        return (
          <div
            key={position}
            className={styles.container}
            data-position={position}
            data-testid={`mg-toast-container-${position}`}>
            {positionedToasts.map((toast) => (
              <ToastContent key={toast.id} toast={toast} />
            ))}
          </div>
        );
      })}
    </>,
    document.body,
  );
};
